'use client'

import { useId, useState } from 'react'
import { useRouter } from 'next/navigation'
import { BCProduct } from '@cerifi/ecomm/bigcommerce/types/product'
import useEnrollmentStore from '@cerifi/state/enrollment-store'
import { Button } from '@cerifi/ui/components/button'
import { Currency } from '@cerifi/ui/components/currency'
import { Divider } from '@cerifi/ui/components/divider'

interface EnrollmentFooterProps {
  tag: React.ReactNode
  title: React.ReactNode
  product: BCProduct
  /** Product to add on (usually the textbook add on), pass in null if they don't want the add on */
  addOnProduct: BCProduct | null
  /** Will disable the button unless the user has chosen a university */
  hasUniversity?: boolean
  /** Will disable the button unless the user has chosen a month (used by instructor led CFP) */
  hasMonth?: boolean
  /** Will disable the button unless the user has chosen an exam start date (used by Capstone and The Dalton Review) */
  hasExamDate?: boolean
  /** Will disable the button unless the user has chosen a location (used by Capstone and The Dalton Review if 'in person') */
  hasLocation?: boolean
}

export const EnrollmentFooter = (props: EnrollmentFooterProps) => {
  const [
    _addToCart,
    university,
    dob,
    showDob,
    month,
    examMonth,
    location,
    showValidation,
    setShowValidation,
  ] = useEnrollmentStore((state) => [
    state.addToCart,
    state.university,
    state.dob,
    state.showDob,
    state.monthToStartLabel,
    state.examMonthLabel,
    state.locationLabel,
    state.showValidation,
    state.setShowValidation,
  ])

  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const id = useId()

  const addToCart = () => {
    if (!!validationMsg()) {
      setShowValidation(true)
      return
    }

    setShowValidation(false)
    setLoading(true)
    _addToCart(props.product, props.addOnProduct || undefined)
      .then(() => router.push('/cart'))
      .catch(() => {
        setLoading(false)
      })
  }

  const validationMsg = () => {
    const requiredFields: string[] = []
    if (props.hasUniversity) {
      if (!university) {
        requiredFields.push('University')
      }
      if (!dob && showDob) {
        requiredFields.push('Date of Birth')
      }
    }
    if (props.hasMonth && !month) {
      requiredFields.push('Course Date / Cohort')
    }

    if (props.hasExamDate && !examMonth) {
      requiredFields.push('Target Exam Month')
    }

    if (props.hasLocation && !location) {
      requiredFields.push('Location')
    }

    if (requiredFields.length > 0) {
      return (
        requiredFields.join(', ') +
        `${requiredFields.length > 1 ? ' are' : ' is'} required`
      )
    }
  }

  return (
    <div className="inset-x-0 bottom-0 bg-white sm:fixed">
      {showValidation && validationMsg() && (
        <div
          className="bg-error-50 py-1 px-12 text-sx text-end font-medium border-b-2 border-b-error-800"
          id={`hint-${id}`}
          data-testid={`enrollment-validation-msg`}
        >
          {validationMsg()}
        </div>
      )}
      <Divider />
      <div className="p-4 sm:py-6 sm:px-12">
        <div className="flex flex-wrap items-center justify-between gap-6 sm:items-center">
          <div className="hidden sm:block">
            <div className="text-sm font-medium">{props.tag}</div>
            <div
              className="text-2xl text-neutral-900 font-serif"
              data-testid="enrollment-title"
            >
              {props.title}
            </div>
          </div>

          <div className="hidden sm:block sm:flex-1" />
          <div>
            <div className="hidden sm:block sm:text-right">Subtotal</div>
            <div className="text-2xl text-neutral-900 font-medium">
              <Currency
                data-testid="enrollment-subtotal"
                dollars={props.product.price + (props.addOnProduct?.price || 0)}
              />
            </div>
          </div>

          <div>
            <Button
              onClick={() => addToCart()}
              isLoading={loading}
              data-testid="enrollment-add-to-cart-btn"
              aria-describedby={`hint-${id}`}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
